/**
 * FP3-053 — Super Admin context hook.
 *
 * Single source of truth for the persistent organization/school
 * context the Super Admin selects from the sidebar dropdown.
 *
 * Storage strategy
 * ────────────────
 *   • URL query params (`?as_org=...&as_school=...`) are the
 *     primary state — every admin surface that already respects
 *     `?as_org=` (FP3-037) continues to work unchanged, and the
 *     new `?as_school=` flag layers on top.
 *   • localStorage mirrors the URL so the selection survives a
 *     full reload (or a fresh tab) without forcing the user to
 *     re-pick. The URL still wins when both differ — a deep link
 *     beats a stale cache.
 *
 * The hook is safe to call from any component (provider / admin /
 * super_admin). Non-super_admin callers always get a "no context"
 * result so nothing leaks across roles.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";

const LS_KEY = "fp3.superAdmin.context.v1";

function _readLs() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return {
      orgId: (parsed.orgId || "").trim() || "",
      schoolId: (parsed.schoolId || "").trim() || "",
    };
  } catch {
    return null;
  }
}

function _writeLs(orgId, schoolId) {
  try {
    if (!orgId && !schoolId) {
      localStorage.removeItem(LS_KEY);
      return;
    }
    localStorage.setItem(
      LS_KEY,
      JSON.stringify({ orgId: orgId || "", schoolId: schoolId || "" })
    );
  } catch {
    // localStorage may be disabled — silent best-effort.
  }
}

/**
 * useSuperAdminContext()
 *
 * Returns:
 *   {
 *     isSuperAdmin: bool,
 *     orgId: string,            // "" when "All Organizations"
 *     schoolId: string,         // "" when no school pinned
 *     orgName: string,          // resolved from /platform/context-tree
 *     schoolName: string,
 *     tree: [{id, name, schools: [{id, name}]}],
 *     loading: bool,
 *     setContext({orgId?, schoolId?}),
 *     clearContext(),
 *   }
 */
export function useSuperAdminContext() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const isSuperAdmin = (user?.permission_role || "") === "super_admin";

  // URL state is the leader. When the URL is empty we fall back to
  // the last value persisted in localStorage and (lazily) write
  // that back into the URL so deep links work cleanly.
  const urlOrg = searchParams.get("as_org") || "";
  const urlSchool = searchParams.get("as_school") || "";

  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(false);

  // ── Bootstrap: pull context tree once per super_admin session. ──
  useEffect(() => {
    if (!isSuperAdmin) {
      setTree([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .getPlatformContextTree()
      .then((r) => {
        if (cancelled) return;
        setTree(Array.isArray(r?.data?.orgs) ? r.data.orgs : []);
      })
      .catch(() => {
        if (cancelled) return;
        setTree([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin]);

  // ── Rehydrate from localStorage when the URL is empty. ─────────
  useEffect(() => {
    if (!isSuperAdmin) return;
    if (urlOrg || urlSchool) return;
    const cached = _readLs();
    if (!cached || (!cached.orgId && !cached.schoolId)) return;
    // Only rehydrate on admin-area routes so a super admin sitting
    // on /platform/dashboard doesn't get silently re-scoped.
    if (!location.pathname.startsWith("/admin/")) return;
    const next = new URLSearchParams(location.search || "");
    if (cached.orgId) next.set("as_org", cached.orgId);
    if (cached.schoolId) next.set("as_school", cached.schoolId);
    navigate(`${location.pathname}?${next.toString()}`, { replace: true });
    // We only want this to run once per page visit — the URL win
    // logic above prevents a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin, location.pathname]);

  // ── Persist URL changes back into localStorage. ────────────────
  useEffect(() => {
    if (!isSuperAdmin) return;
    _writeLs(urlOrg, urlSchool);
  }, [isSuperAdmin, urlOrg, urlSchool]);

  // ── Name resolution from the tree. ─────────────────────────────
  const { orgName, schoolName } = useMemo(() => {
    if (!urlOrg) return { orgName: "", schoolName: "" };
    const org = tree.find((o) => o.id === urlOrg);
    if (!org) return { orgName: "", schoolName: "" };
    let school = "";
    if (urlSchool) {
      const s = (org.schools || []).find((s) => s.id === urlSchool);
      if (s) school = s.name || "";
    }
    return { orgName: org.name || "", schoolName: school };
  }, [tree, urlOrg, urlSchool]);

  const setContext = useCallback(
    ({ orgId, schoolId } = {}) => {
      const next = new URLSearchParams(location.search || "");
      // Setting orgId="" clears both org + school. Setting only
      // schoolId without an org is invalid — drop it.
      const cleanOrg = (orgId || "").trim();
      const cleanSchool = (schoolId || "").trim();
      if (cleanOrg) {
        next.set("as_org", cleanOrg);
        if (cleanSchool) {
          next.set("as_school", cleanSchool);
        } else {
          next.delete("as_school");
        }
      } else {
        next.delete("as_org");
        next.delete("as_school");
      }
      const qs = next.toString();
      navigate(`${location.pathname}${qs ? `?${qs}` : ""}`);
    },
    [location.pathname, location.search, navigate]
  );

  const clearContext = useCallback(() => {
    setContext({ orgId: "", schoolId: "" });
  }, [setContext]);

  return {
    isSuperAdmin,
    orgId: isSuperAdmin ? urlOrg : "",
    schoolId: isSuperAdmin ? urlSchool : "",
    orgName,
    schoolName,
    tree,
    loading,
    setContext,
    clearContext,
  };
}
